import clsx from "clsx";
import { formatMonthShortDe } from "@/lib/heroDocumentReports";
import "./auswertungen.css";

export interface MonthlyBarSeries {
  key: string;
  label: string;
  /** CSS-Modifier, z.B. "paid" → .eh-docreport-bar__segment--paid */
  variant: string;
  /** Nicht in der Haupt-Summe, sondern als Klammerwert darunter (z. B. Entwürfe). */
  excludeFromTotal?: boolean;
}

export interface MonthlyBarDatum {
  month: string;
  values: Record<string, number>;
}

/**
 * Gestapelte oder gruppierte Monats-Balken (12 Spalten) im Stil der übrigen
 * Auswertungen – reine CSS-Flex-Umsetzung ohne Chart-Library.
 */
export function MonthlyStackedBars({
  data,
  series,
  formatValue,
  emptyText = "Keine Daten im gewählten Zeitraum.",
  mode = "stacked",
}: {
  data: MonthlyBarDatum[];
  series: MonthlyBarSeries[];
  formatValue: (value: number) => string;
  emptyText?: string;
  /** stacked = Segmente übereinander; grouped = nebeneinander (Vergleich). */
  mode?: "stacked" | "grouped";
}) {
  const totals = data.map((d) =>
    series.reduce((sum, s) => sum + Math.max(d.values[s.key] ?? 0, 0), 0),
  );
  const displayTotals = data.map((d) =>
    series
      .filter((s) => !s.excludeFromTotal)
      .reduce((sum, s) => sum + Math.max(d.values[s.key] ?? 0, 0), 0),
  );
  const extraTotals = data.map((d) =>
    series
      .filter((s) => s.excludeFromTotal)
      .reduce((sum, s) => sum + Math.max(d.values[s.key] ?? 0, 0), 0),
  );
  const maxStacked = Math.max(...totals, 0);
  const maxGrouped = Math.max(
    0,
    ...data.flatMap((d) => series.map((s) => Math.max(d.values[s.key] ?? 0, 0))),
  );
  const max = mode === "grouped" ? maxGrouped : maxStacked;

  if (max <= 0) {
    return <p className="text-sm text-label-secondary">{emptyText}</p>;
  }

  return (
    <div className="space-y-3">
      <div className="eh-docreport-chart" role="img" aria-label="Monatsverlauf">
        {data.map((d, idx) => {
          const total = totals[idx] ?? 0;
          const displayTotal = displayTotals[idx] ?? 0;
          const extraTotal = extraTotals[idx] ?? 0;
          const title = [
            formatMonthShortDe(d.month),
            ...series
              .filter((s) => (d.values[s.key] ?? 0) > 0)
              .map((s) => `${s.label}: ${formatValue(d.values[s.key] ?? 0)}`),
            mode === "stacked" && displayTotal > 0 ? `Gesamt: ${formatValue(displayTotal)}` : null,
          ]
            .filter(Boolean)
            .join("\n");

          if (mode === "grouped") {
            return (
              <div key={d.month} className="eh-docreport-chart__col" title={title}>
                <div className="eh-docreport-chart__bar-area eh-docreport-chart__bar-area--grouped">
                  {series.map((s) => {
                    const value = Math.max(d.values[s.key] ?? 0, 0);
                    return (
                      <div
                        key={s.key}
                        className={clsx(
                          "eh-docreport-chart__bar eh-docreport-chart__bar--grouped",
                          "eh-docreport-bar__segment",
                          `eh-docreport-bar__segment--${s.variant}`,
                        )}
                        style={{ height: value > 0 ? `${(value / max) * 100}%` : "0%" }}
                      />
                    );
                  })}
                </div>
                <div className="eh-docreport-chart__value" />
                <div className="eh-docreport-chart__label">{formatMonthShortDe(d.month)}</div>
              </div>
            );
          }

          return (
            <div key={d.month} className="eh-docreport-chart__col" title={title}>
              <div className="eh-docreport-chart__bar-area">
                {total > 0 ? (
                  <div
                    className="eh-docreport-chart__bar"
                    style={{ height: `${(total / max) * 100}%` }}
                  >
                    {series.map((s) => {
                      const value = Math.max(d.values[s.key] ?? 0, 0);
                      if (value <= 0) return null;
                      return (
                        <div
                          key={s.key}
                          className={clsx(
                            "eh-docreport-bar__segment",
                            `eh-docreport-bar__segment--${s.variant}`,
                          )}
                          style={{ height: `${(value / total) * 100}%` }}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="eh-docreport-chart__value">
                {displayTotal > 0 ? formatValue(displayTotal) : ""}
                {extraTotal > 0 ? (
                  <span className="eh-docreport-chart__value-extra">({formatValue(extraTotal)})</span>
                ) : null}
              </div>
              <div className="eh-docreport-chart__label">{formatMonthShortDe(d.month)}</div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-xs text-label-secondary">
            <span
              className={clsx(
                "inline-block h-2.5 w-2.5 rounded-sm",
                "eh-docreport-bar__segment",
                `eh-docreport-bar__segment--${s.variant}`,
              )}
              aria-hidden
            />
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
